import React, { useEffect, useState } from 'react';
import { Layout, Typography, Button, Dropdown, Avatar, message, MenuProps } from 'antd';
import { ArrowLeftOutlined, LogoutOutlined, UserOutlined, MenuUnfoldOutlined, MenuFoldOutlined, ClockCircleOutlined, GlobalOutlined, DownOutlined } from '@ant-design/icons';
 import { useTranslation } from 'react-i18next';  // Importer i18next
 import MAFlag from '../../assets/images/flag/morocco.png';
 import FRFlag from '../../assets/images/flag/france.png';
 import USFlag from '../../assets/images/flag/usa.png';
import { useTilmiAuth } from '../../hooks/useTilmiAuth';
import { useNavigate } from 'react-router-dom';
import { useIsTablet } from '../../hooks/useIsTablet';
const { Header } = Layout;
const { Title } = Typography;

const palette = {
    primary: '#2992BF',
    secondary: '#8BBE1E',
    background: '#f5f9fb',
    border: '#e0e7f1',
    textPrimary: '#0f172a',
    textMuted: '#6b7280',
};

interface NavbarProps {
    title: string;
    onBackNavigation?: () => void;
    showBackButton?: boolean;
    isCollapsed: boolean;
    onToggleSidebar: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ title, onBackNavigation, showBackButton = false, isCollapsed, onToggleSidebar }) => {
    const navigate = useNavigate();
    const { i18n, t } = useTranslation();
    const { logout, getTimeRemaining, getFullName, setLang } = useTilmiAuth();
    const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false); // État pour le chargement
    const isMobile = window.innerWidth <= 768;
    const isTablet = useIsTablet();


    const timeRemaining: number = getTimeRemaining();
    const fullName: string | null = getFullName();
    const [timeLeft, setTimeLeft] = useState<number>(timeRemaining);

    const languageLookup: Record<string, { flag: string; label: string }> = {
        fr: { flag: FRFlag, label: 'Français' },
        en: { flag: USFlag, label: 'English' },
        ar: { flag: MAFlag, label: 'العربية' },
    };
    const activeLanguage = languageLookup[i18n.language] || languageLookup.fr;

    const profileMenuItems: MenuProps['items'] = [
        {
            key: 'profile-name',
            label: <span style={{ fontWeight: 600 }}>{fullName || t('navbar.noName', 'Explorateur')}</span>,
            disabled: true,
        },
        { type: 'divider' },
        {
            key: 'logout',
            icon: <LogoutOutlined />,
            label: t('navbar.logout'),
        },
    ];

    const styles = {
        header: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 24px',
            margin: '12px auto 0',
            borderRadius: 24,
            background: 'linear-gradient(135deg, rgba(41,146,191,0.12), rgba(139,190,30,0.12))',
            border: `1px solid ${palette.border}`,
            boxShadow: '0 18px 32px rgba(15,23,42,0.08)',
            width: 'calc(100% - 48px)',
            maxWidth: '1080px',
            gap: 16,
        },
        leftCluster: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minWidth: 0,
        },
        logoButton: {
            border: 'none',
            background: 'transparent',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
        },
        logo: {
            height: 32,
        },
        iconButton: {
            width: 38,
            height: 38,
            borderRadius: 14,
            border: `1px solid ${palette.border}`,
            background: 'linear-gradient(135deg, #ffffff, #f6fafc)',
            color: palette.textPrimary,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 18px rgba(15,23,42,0.08)',
        },
        titleStack: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: 4,
        },
        eyebrow: {
            display: 'none',
        },
        title: {
            display: 'none',
        },
        rightCluster: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
        },
        pill: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 12px',
            borderRadius: 14,
            background: 'linear-gradient(135deg, #eef6ff, #ffffff)',
            border: `1px solid ${palette.border}`,
            color: '#1d4ed8',
            fontSize: 12,
            fontWeight: 600,
        },
        languageTrigger: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 14px',
            borderRadius: 16,
            border: `1px solid ${palette.border}`,
            background: 'linear-gradient(135deg, #ffffff, #f6fafc)',
            color: palette.textPrimary,
            boxShadow: '0 8px 18px rgba(15,23,42,0.08)',
        },
        profile: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            padding: '6px 14px',
            borderRadius: 18,
            border: `1px solid ${palette.border}`,
            background: 'linear-gradient(135deg, #ffffff, #f6fafc)',
            cursor: 'pointer',
            boxShadow: '0 10px 20px rgba(15,23,42,0.08)',
        },
        profileName: {
            fontSize: 13,
            fontWeight: 600,
            color: palette.textPrimary,
            maxWidth: 160,
            whiteSpace: 'nowrap' as const,
            overflow: 'hidden' as const,
            textOverflow: 'ellipsis' as const,
        },
        profileAvatar: {
            width: 28,
            height: 28,
            background: palette.primary,
        },
    };

    const languageMenu: MenuProps['items'] = [
        {
            key: 'fr',
            label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={FRFlag} alt="Français" style={{ width: 18 }} />
                    Français
                </div>
            ),
        },
        {
            key: 'en',
            label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={USFlag} alt="English" style={{ width: 18 }} />
                    English
                </div>
            ),
        },
        {
            key: 'ar',
            label: (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <img src={MAFlag} alt="العربية" style={{ width: 18 }} />
                    العربية
                </div>
            ),
        },
    ];

    const handleProfileMenuClick: MenuProps['onClick'] = ({ key }) => {
        if (key === 'logout') {
            handleLogout();
        }
    };

    const handleLanguageMenuClick: MenuProps['onClick'] = ({ key }) => {
        if (typeof key === 'string') {
            handleLanguageChange(key);
        }
    };

    // timer
    useEffect(() => {
        // Si timeRemaining est -1, on ne lance pas le minuteur
        if (timeRemaining === -1 || timeRemaining > 5400) {
            return;
        }

        // Fonction qui décrémente le temps restant toutes les secondes
        const timerId = setInterval(() => {
            setTimeLeft((prevTime) => {
                if (prevTime <= 1) {
                    clearInterval(timerId); // Arrêter le minuteur quand il atteint 0
                    logout()
                    return 0; // Retourner 0 pour s'assurer que le temps est bien à 0
                }
                return prevTime - 1; // Décrémenter le temps restant
            });
        }, 1000); // Mise à jour toutes les 1000 ms (1 seconde)

        // Nettoyage du minuteur lorsque le composant est démonté
        return () => clearInterval(timerId);

        // eslint-disable-next-line
    }, [timeRemaining, navigate]);

    // Fonction pour changer la langue
    const handleLanguageChange = async (value: string) => {
        try {
            await setLang(value);
            i18n.changeLanguage(value);
        } catch (err: any) {
            message.error(err?.response?.data ?? err?.message ?? 'Erreur lors du changement de langue');
        }
    };

    const handleLogout = async () => {
        if (isLoggingOut) return;
        setIsLoggingOut(true);
        try {
            await logout();
        } catch (err: any) {
            message.error(err?.response?.data ?? err?.message ?? 'Erreur de déconnexion');
        } finally {
            setIsLoggingOut(false);
        }
    };


    return (
        <Header style={styles.header}>
            <div style={styles.leftCluster}>
                <button
                    type="button"
                    style={styles.logoButton}
                    onClick={() => navigate('/program')}
                    aria-label={t('navbar.goHome', 'Go to home')}
                >
                    <img src="/images/logo_400.png" alt="Tilmicode" style={styles.logo} />
                </button>

                {isTablet && (
                    <Button
                        type="text"
                        onClick={onToggleSidebar}
                        style={styles.iconButton}
                        aria-label={t('navbar.toggleSidebar', 'Toggle sidebar')}
                    >
                        {isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                    </Button>
                )}

                {showBackButton && (
                    <Button
                        type="text"
                        onClick={onBackNavigation || (() => navigate(-1))}
                        style={styles.iconButton}
                        aria-label={t('navbar.back', 'Go back')}
                    >
                        <ArrowLeftOutlined />
                    </Button>
                )}
            </div>

            <div style={styles.rightCluster}>
                <Dropdown
                    menu={{ items: languageMenu, onClick: handleLanguageMenuClick }}
                    trigger={['click']}
                >
                    {isMobile ? (
                        <Button type="text" style={styles.iconButton}>
                            <GlobalOutlined />
                        </Button>
                    ) : (
                        <button type="button" style={styles.languageTrigger}>
                            <img src={activeLanguage.flag} alt={activeLanguage.label} style={{ width: 18 }} />
                            <span>{activeLanguage.label}</span>
                            <DownOutlined style={{ fontSize: 10, color: palette.textMuted }} />
                        </button>
                    )}
                </Dropdown>

                {timeLeft > 0 && timeLeft <= 5400 && (
                    <div style={styles.pill}>
                        <ClockCircleOutlined />
                        <span>{new Date(timeLeft * 1000).toISOString().slice(11, 19)}</span>
                    </div>
                )}

                <Dropdown
                    menu={{ items: profileMenuItems, onClick: handleProfileMenuClick }}
                    placement="bottomRight"
                >
                    <div style={styles.profile}>
                        <Avatar style={styles.profileAvatar} icon={<UserOutlined />} />
                    </div>
                </Dropdown>
            </div>
        </Header>
    );
};

export default Navbar;
